/**
 * Was landet wirklich auf dem ZA-Konto? — Die Rechnung des RZL-Exports.
 *
 * ANLASSFALL 2026-08-13 (Franka, Rauch Gernot 07/2026): Die Karte zeigte
 * -11,1 h, der Export lieferte -12,7 h. Gemessen über den Bestand: **159 von
 * 190 Karten** zeigten einen anderen Wert, als exportiert wurde — über fünf
 * Monate, mit Ausschlägen bis 192 h.
 *
 * Ursache: Die Zahl wurde an zwei Stellen unterschiedlich gerechnet. Die
 * Anzeige nahm die MONATS-Differenz (`totals.ueberstunden_za_h`), der Export
 * (hub/src/export/rzl.ts) summiert über die WOCHEN und lässt die auf den
 * Folgemonat aufgeschobene Woche aus. Die Monats-Differenz zählt die Tage
 * dieser Woche mit — obwohl die Monatswechsel-Regel gerade sagt, dass sie erst
 * im Folgemonat zählen.
 *
 * WARUM DIE ANZEIGE FOLGT UND NICHT UMGEKEHRT: Der Export ist das gelieferte
 * Ergebnis; die Anzeige existiert, um ihn zu kontrollieren. Eine Kontrolle, die
 * eine andere Zahl zeigt als das Kontrollierte, ist wertlos. Diese Funktion
 * ändert deshalb KEINEN exportierten Wert — sie beendet nur den Widerspruch.
 *
 * Welche der beiden Rechnungen die fachlich richtige ist, ist eine
 * Abrechnungsfrage und liegt bei Sibit zur Bestätigung (Mail 13.08.). Fällt die
 * Entscheidung auf die Monats-Rechnung, ändern sich Anzeige UND Export
 * gemeinsam — dann ist diese Stelle der eine Ort dafür.
 */

export type PayMode = "cash" | "za" | "pauschale";

interface WocheFuerZa {
  ist_h_sum?: number;
  soll_h_sum?: number;
  mehrstunden_h?: number;
  ueberstunden_50_h?: number;
  cross_month_state?: string;
}

/** Saldo über die Wochen — aufgeschobene Wochen zählen im Folgemonat. */
export function zaWochenSaldo(weeks: ReadonlyArray<WocheFuerZa> | null | undefined): number {
  let saldo = 0;
  for (const w of weeks ?? []) {
    if (w.cross_month_state === "deferred_to_next") continue;
    const diff = +(((w.ist_h_sum ?? 0) - (w.soll_h_sum ?? 0))).toFixed(2);
    // Separat ausgewiesene Ü50 sind auch bei Tages-Überstunden und in
    // Null-/Minus-Wochen vollständig aus dem reinen ZA-Saldo herauszunehmen.
    // Nicht nochmals von mehrstunden_h abziehen: neue Karten sind dort bereits
    // korrigiert. Ist-Soll-Ü50 funktioniert für alte und neue Berechnungen.
    saldo += typeof w.ueberstunden_50_h === "number"
      ? +(diff - w.ueberstunden_50_h).toFixed(2)
      : diff >= 0 ? (w.mehrstunden_h ?? 0) : diff;
  }
  return +saldo.toFixed(2);
}

/**
 * Der Wert, den der Export aufs ZA-Konto schreibt.
 *  - pauschale: 0 (die Vertragspauschale deckt alles ab)
 *  - cash:      nur der Wochen-Saldo (die 50%-Überstunden werden bar ausbezahlt)
 *  - za:        Wochen-Saldo + 50%-Überstunden (beide landen auf dem Konto)
 * Fehlen die Wochen (alte Karte ohne `weeks`), wird auf den gespeicherten Wert
 * zurückgefallen — lieber der bisherige Wert als gar keiner.
 */
export function zaKontoWieExport(
  calculation: { weeks?: ReadonlyArray<WocheFuerZa>; totals?: Record<string, number> } | null | undefined,
  payMode: PayMode | string | null | undefined,
): number | null {
  const totals = calculation?.totals;
  if (!totals) return null;
  if (payMode === "pauschale") return 0;
  const weeks = calculation?.weeks;
  if (!weeks || weeks.length === 0) {
    const gespeichert = totals["ueberstunden_za_h"];
    return typeof gespeichert === "number" ? gespeichert : null;
  }
  const saldo = zaWochenSaldo(weeks);
  if (payMode === "za") return +(saldo + (totals["ueberstunden_50_total_h"] ?? 0)).toFixed(2);
  return saldo;
}
